import { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { Badge, Flex, ScrollArea, Spinner, Text } from "@radix-ui/themes";
import { TextWithTooltip } from "@/components/shared/tooltip";
import { Search } from "@/components/shared/inputs/search";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { useVisibleInNearestScrollArea } from "@/v2/shared/hooks/useVisibleInNearestScrollArea";
import styles from "./InteractionsList.module.scss";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { TopicFormatter } from "@/v2/components/formatters/TopicFormatter";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { CheckIcon, ValueIcon } from "@radix-ui/react-icons";

interface InteractionItemProps {
  interaction: ExploreInteractionRow;
  selectedInteractionId?: string;
  onSelect: (interactionId: string) => void;
}

function InteractionItem({
  interaction,
  selectedInteractionId,
  onSelect,
}: InteractionItemProps) {
  const handleSelect = useCallback(() => {
    onSelect(interaction.id);
  }, [interaction.id, onSelect]);

  return (
    <Flex
      key={interaction.id}
      p="3"
      pt="28px"
      direction="column"
      gap="1"
      onClick={handleSelect}
      className={cx(styles.interaction, {
        [styles.active]: selectedInteractionId === interaction.id,
      })}
    >
      <Flex justify="between" align="center" gap="2">
        <TextWithTooltip
          size="2"
          color="gray"
          weight="medium"
          highContrast
          truncate
        >
          {interaction.subject}
        </TextWithTooltip>

        <Flex align="center" gap="1">
          <Text size="1">
            <AvgQaScoreFormatter value={interaction.avgQaScore} />
          </Text>

          {interaction.validationStatus ? (
            <Badge size="1" color="green" variant="surface">
              <CheckIcon />
            </Badge>
          ) : (
            <Badge size="1" color="gray" variant="surface">
              <ValueIcon />
            </Badge>
          )}
        </Flex>
      </Flex>

      <Text size="1" color="gray">
        {`#${interaction.displayId}`}
      </Text>

      <Flex align="center" gap="2">
        {interaction.topic && (
          <TextWithTooltip
            size="1"
            color="gray"
            highContrast
            truncate
            className="px-[6px] py-[2px] bg-[--gray-a3] rounded-sm"
          >
            <TopicFormatter value={interaction.topic} />
          </TextWithTooltip>
        )}

        <Text size="1" color="gray" className="flex-shrink-0">
          <DateFormatter value={interaction.createdAt} />
        </Text>
      </Flex>
    </Flex>
  );
}

interface InteractionsListProps {
  loading: boolean;
  interactions?: ExploreInteractionRow[];
  totalCount?: number;
  selectedInteractionId?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onSelect: (interactionId: string) => void;
}

export function InteractionsList({
  loading,
  interactions,
  totalCount,
  selectedInteractionId,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  onSelect,
}: InteractionsListProps) {
  const [searchPhrase, setSearchPhrase] = useState("");
  const { ref: sentinelRef, inView: isSentinelVisible } =
    useVisibleInNearestScrollArea();

  const filteredInteractions = useMemo(() => {
    if (interactions == null) {
      return [];
    }

    return interactions.filter(
      (interaction) =>
        interaction.subject?.toLowerCase()
          .includes(searchPhrase.toLowerCase()) ||
        interaction.displayId.toLowerCase().includes(searchPhrase.toLowerCase())
    );
  }, [interactions, searchPhrase]);

  useEffect(() => {
    if (isSentinelVisible && hasNextPage && !isFetchingNextPage) {
      onLoadMore?.();
    }
  }, [isSentinelVisible, hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <Flex width="100%" height="100%" direction="column" overflow="hidden">
      <Flex p="3" direction="column" gap="2" className="border-b">
        <Flex justify="between" align="center" gap="3">
          <Text size="2" color="gray" weight="medium" highContrast>
            Sampled Items
          </Text>

          {(totalCount ?? interactions?.length ?? 0) > 0 && (
            <Badge>{totalCount ?? interactions?.length}</Badge>
          )}
        </Flex>

        <Search
          initialValue={searchPhrase}
          placeholder="Search interactions"
          setSearchPhrase={setSearchPhrase}
          debounce={50}
        />
      </Flex>

      <Flex
        flexGrow="1"
        direction="column"
        gap="1"
        className={styles.interactionsList}
        overflow="hidden"
      >
        <Flex height="100%" overflow="hidden">
          <ScrollArea
            scrollbars={"vertical"}
            style={{ width: "100%", height: "100%" }}
          >
            <Flex p="3" height="100%" direction="column" gap="1">
              {loading ? (
                <Flex
                  flexGrow="1"
                  p="4"
                  gap="2"
                  justify="center"
                  align="center"
                >
                  <Spinner size="2" />
                  <Text size="2" color="gray">
                    Loading interactions...
                  </Text>
                </Flex>
              ) : (
                <>
                  {filteredInteractions?.length === 0 && !hasNextPage && (
                    <Flex flexGrow="1" p="4" justify="center">
                      <Text size="2" color="gray">
                        No Items Found
                      </Text>
                    </Flex>
                  )}

                  {filteredInteractions?.map((interaction) => (
                    <InteractionItem
                      interaction={interaction}
                      selectedInteractionId={selectedInteractionId}
                      onSelect={onSelect}
                    />
                  ))}

                  <Flex ref={sentinelRef} justify="center" p="3">
                    {isFetchingNextPage && <Spinner size="2" />}
                  </Flex>
                </>
              )}
            </Flex>
          </ScrollArea>
        </Flex>
      </Flex>
    </Flex>
  );
}
